<script setup lang="ts">
// Support action: move a participant into another project of the same trainer (RDT-41).
// The backend owns the compatibility rules (target must not be a direct-link project, product and
// recipients must be compatible), so the target list comes from /projects/{trainer}/{participant}/compatible
// and is never assembled here. The rules are spelled out in the dialog so support can explain a
// missing target to the customer instead of guessing.
import { computed, ref, type PropType } from "vue";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";
import { ProfileAdministrationAPI } from "../../api";
import { Participant, ParticipantActivityType, Project } from "../../types/PersologOnlineTypes.ts";

const props = defineProps({
    participant: {
        type: Object as PropType<Participant>,
        required: true,
    },
    /** Passed in where the caller already has it; otherwise the project is resolved on open. */
    project: {
        type: Object as PropType<Project | null>,
        default: null,
    },
});

const emit = defineEmits<{ moved: [participant: Participant] }>();

const { t } = useI18n();
const toast = useToast();

const MOVE_ERROR_KEY = "project-details.participants.move.description.error";

const dialogVisible = ref(false);
const infoVisible = ref(false);
const loading = ref(false);
const compatibleProjects = ref<Project[]>([]);
const selectedTargetProject = ref<Project | null>(null);
const errorMessage = ref<string | null>(null);

const participantName = computed(() => {
    const name = `${props.participant.firstName ?? ""} ${props.participant.name ?? ""}`.trim();
    return name || props.participant.email;
});

const showError = () => {
    errorMessage.value = t(MOVE_ERROR_KEY);
};

const toggleInfo = (collapsed: boolean) => {
    infoVisible.value = !collapsed;
};

/** Best effort: the move itself succeeded, so a failing log entry must not read as a failed move. */
const logMove = async (participantId: number) => {
    try {
        await ProfileAdministrationAPI.logParticipantActivity(participantId, ParticipantActivityType.PARTICIPANT_MOVED);
    } catch (error) {
        console.warn("Participant moved, but the activity could not be logged", error);
    }
};

const openDialog = async () => {
    dialogVisible.value = true;
    selectedTargetProject.value = null;
    compatibleProjects.value = [];
    errorMessage.value = null;

    const participantId = props.participant.id;
    if (!participantId) return;

    loading.value = true;
    try {
        const project = props.project ?? (await ProfileAdministrationAPI.fetchProject(props.participant.projectId));
        if (!project.trainer) {
            showError();
            return;
        }
        const projects = await ProfileAdministrationAPI.fetchCompatibleProjects(project.trainer, participantId);
        // The current project is compatible with itself and would be a no-op target.
        compatibleProjects.value = projects.filter((candidate) => candidate.id !== props.participant.projectId);
    } catch (error) {
        console.error("Could not load compatible projects", error);
        showError();
    } finally {
        loading.value = false;
    }
};

const moveParticipant = async () => {
    const targetProject = selectedTargetProject.value;
    const participantId = props.participant.id;
    if (!targetProject?.id || !participantId) return;

    loading.value = true;
    errorMessage.value = null;
    try {
        const movedParticipant = await ProfileAdministrationAPI.moveParticipant(targetProject.id, participantId);
        await logMove(participantId);
        toast.add({
            severity: "success",
            summary: t("messages.success"),
            detail: t("messages.move.success", { participant: participantName.value, project: targetProject.name }),
            life: 4000,
        });
        dialogVisible.value = false;
        emit("moved", movedParticipant);
    } catch (error) {
        console.error("Could not move participant", error);
        showError();
    } finally {
        loading.value = false;
    }
};
</script>

<template>
    <Button
        :data-cy="`move-participant-button-${participant.id}`"
        icon="pi pi-arrow-right-arrow-left"
        rounded
        outlined
        severity="secondary"
        class="mr-2"
        :aria-label="t('project-details.participants.actionsLabels.move-participant')"
        v-tooltip="t('project-details.participants.actionsLabels.move-participant')"
        :disabled="!participant.id"
        @click="openDialog"
    />
    <Dialog
        v-model:visible="dialogVisible"
        modal
        :draggable="false"
        :dismissable-mask="true"
        :header="t('project-details.participants.actionsLabels.move-participant')"
        :style="{ width: 'min(40rem, 95vw)' }"
    >
        <div class="flex flex-col gap-4">
            <div>
                <strong>{{ participantName }}</strong>
                <span v-if="participant.email && participant.email !== participantName">
                    ({{ participant.email }})
                </span>
            </div>

            <Message v-if="errorMessage" severity="error" :closable="false">{{ errorMessage }}</Message>

            <div v-if="loading && compatibleProjects.length === 0" class="flex items-center gap-2">
                <i class="pi pi-spin pi-spinner"></i>
                <span>{{ t("project-details.participants.move.description.searching") }}</span>
            </div>
            <template v-else-if="!errorMessage">
                <span v-if="compatibleProjects.length === 0">
                    {{ t("project-details.participants.move.description.failed") }}
                </span>
                <template v-else>
                    <span>{{ t("project-details.participants.move.description.success") }}</span>
                    <Select
                        :data-cy="`move-participant-select-${participant.id}`"
                        v-model="selectedTargetProject"
                        :options="compatibleProjects"
                        option-label="name"
                        :placeholder="t('projects.label')"
                        :loading="loading"
                        class="w-full"
                    >
                        <template #option="slotProps">
                            <span>{{ slotProps.option.name }}</span>
                            <Tag
                                class="ml-2"
                                severity="info"
                                style="font-weight: normal"
                                :value="slotProps.option.product.toUpperCase()"
                            />
                        </template>
                    </Select>
                </template>
            </template>

            <Fieldset
                :legend="t('project-details.participants.move.info.heading')"
                toggleable
                :collapsed="!infoVisible"
                @update:collapsed="toggleInfo"
            >
                <p>{{ t("project-details.participants.move.info.intro") }}</p>
                <ul>
                    <li>
                        <strong>{{ t("project-details.participants.move.info.directLinkHeading") }}</strong><br />
                        {{ t("project-details.participants.move.info.directLinkText") }}
                    </li>
                    <li>
                        <strong>{{ t("project-details.participants.move.info.productHeading") }}</strong><br />
                        {{ t("project-details.participants.move.info.productText") }}
                    </li>
                    <li>
                        <strong>{{ t("project-details.participants.move.info.recipientsHeading") }}</strong><br />
                        {{ t("project-details.participants.move.info.recipientsText") }}
                    </li>
                    <li>
                        <strong>{{ t("project-details.participants.move.info.additionalRecipientHeading") }}</strong
                        ><br />
                        {{ t("project-details.participants.move.info.additionalRecipientText") }}
                    </li>
                </ul>
            </Fieldset>
        </div>
        <template #footer>
            <Button
                :label="t('buttons.cancel')"
                icon="pi pi-times"
                severity="secondary"
                :disabled="loading"
                @click="dialogVisible = false"
            />
            <Button
                :data-cy="`move-participant-confirm-${participant.id}`"
                :label="t('buttons.move')"
                icon="pi pi-arrow-right"
                :disabled="!selectedTargetProject || loading"
                :loading="loading"
                @click="moveParticipant"
            />
        </template>
    </Dialog>
</template>
